import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global, useMenuStore } from "@shared/libs/Global";
import SearchPanel from "@widgets/SearchPanel";
import { menuInfo } from "@shared/type/CommonType";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { showDialog } from "@widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import SnTree from "@shared/components/GridV2/SnTree";
import { ColDef } from "ag-grid-community";
import { dateToString } from "@shared/utils/DateUtils";
import SnResizeBar from "@shared/components/SnResizeBar";
import CommonSpinner from "@widgets/CommonSpinner";

interface Props {
  Menuprops: menuInfo;
}

const SN_MAS_ORG_REG_006 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();
  const { menuItems } = useMenuStore();
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
    grid: React.createRef(),
  });

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    tree: [],
    grid: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    tree: [],
    grid: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          tree: rtn.grid.grid1,
          grid: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    try {
      const modifiedRowsH = gridRefs.current["grid"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result = await onSave();
          if (!result) return;
        }
      }

      const params = {
        id: "sn_mas_org_reg_006_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["tree"].current.resetUpdate();
      gridRefs.current["grid"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      if (data) {
        const treeData = convertToTreeData(data[0]);
        setDataSources({
          tree: treeData,
          grid: data[1] || [],
        });
      }
    } catch (error) {}
  };

  const onInsert = async () => {
    const selectedRows = gridRefs.current["tree"].current.getSelectedNodes();
    if (selectedRows.length === 0) {
      await showDialog("alter", "부서를 선택해주세요.");
      return;
    }
    try {
      const selectedRow = selectedRows[0].data;
      if (selectedRow.cd_dept.includes("biz_")) {
        await showDialog("alter", "사업장 하위의 부서를 선택해주세요.");
        return;
      }

      const cd_biz = selectedRow.cd_biz.replace("biz_", "");
      const today = dateToString(new Date());
      const selectedFormData = searchRef.current.getFormdata();
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: cd_biz,
        cd_dept: selectedRows[0].data?.cd_dept,
        nm_dept: selectedRows[0].data?.nm_dept,
        yn_end: "Y",
        yn_use: "Y",
        yn_grw: "Y",
        yn_cd_gp: "Y",
        am_labor: 0,
        dt_start: today,
        dt_join: today,
        tp_rdtax: "",
        yn_disable: "N",
        yn_annu: "N",
        yn_hins: "N",
        yn_eins: "N",
        yn_indus: "N",
        yn_scired: "N",
        yn_fwork: "N",
        yn_njob: "N",
        yn_blue: "N",
        yn_blue_add: "N",
        yn_wife: "N",
        yn_woman: "N",
        yn_hhold: "N",
        yn_fsingle: "N",
        yn_dwork: "N",
        yn_religion: "N",
        fg_work: selectedFormData.fg_work,
      };

      gridRefs.current["grid"].current?.addRow(newRow);

      const rowCount = gridRefs.current["grid"].current.getDisplayedRowCount();
      const selectedTreeNode =
        gridRefs.current["tree"].current.getSelectedNodes();
      if (rowCount === 0 && selectedTreeNode && selectedTreeNode[0]) {
        onFocusedRowChanged(selectedTreeNode[0].data);
      }
    } catch (error: any) {}
  };

  const onDelete = () => {
    const rowCount = gridRefs.current["grid"].current.getDisplayedRowCount();
    if (rowCount == 0) return;
    gridRefs.current["grid"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const gridGetChanges = gridRefs.current["grid"].current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const hasInvalidcddemp = [
        ...(gridGetChanges.insert || []),
        ...(gridGetChanges.update || []),
      ].some((item) => item.cd_emp && item.cd_emp.length > 20);

      if (hasInvalidcddemp) {
        await showDialog("alter", "사원코드가 20자리를 초과하였습니다.");
        return false;
      }

      const hasInvalidnopostr = [
        ...(gridGetChanges.insert || []),
        ...(gridGetChanges.update || []),
      ].some((item) => item.no_post_r && item.no_post_r.length > 6);

      if (hasInvalidnopostr) {
        await showDialog("alter", "우편번호가 6자리를 초과하였습니다.");
        return false;
      }

      const SaveData = {
        mas_emp: gridRefs.current["grid"].current.getChange(),
      };
      await fnSave("SN_MAS_ORG_REG_006_SAVE_01", SaveData);
      gridRefs.current["grid"].current.resetUpdate();
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "부서",
      field: "nm_dept",
      flex: 1, // 화면 가득 차게
      minWidth: 300,
      editable: false,
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
      },
    };
  }, []);

  const onFocusedRowChanged = (rowData: any) => {
    const api = gridRefs.current["grid"].current;
    if (!api || !rowData) return;

    const cdDept = rowData.cd_dept;
    api.resetFilters();

    // 사업장 클릭
    if (!cdDept || cdDept.includes("biz_")) {
      const cdBiz = cdDept?.replace("biz_", "");
      api.applyFilters({
        cd_biz: cdBiz,
      });
      api.selectAndFocusRow(0);
      return;
    }

    // 부서 클릭 → 해당 부서만
    api.applyFilters({
      cd_dept: [cdDept],
    });

    api.selectAndFocusRow(0);
  };

  const getRowId = useCallback(
    (params: any) => String(params.data.cd_dept),
    []
  );

  const onGrid1BtnsClick = async (id: string) => {
    const api = gridRefs.current["tree"].current;
    if (!api) return;

    if (id === "btnOpen") {
      api.setExpandedAll(true);
    }
    if (id === "btnClose") {
      api.setExpandedAll(false);
    }
  };
  const onGrid2BtnsClick = async (id: string) => {
    if (id !== "btnImport") return;

    setLoading(true);
    try {
      const { cd_biz } = searchRef.current.getFormdata();
      if (!cd_biz) {
        await showDialog("alter", "사업장을 선택해 주세요.");
        return;
      }

      const result = await callApi("SN_MAS_ORG_REG_006_btnImport", {
        // cd_firm은 SNOTES 회사코드(연동설정 조회용), coCd는 더존 회사코드로 서로 다른 체계다.
        cd_firm: userInfo.cd_firm,
        cd_biz,
        body: {
          coCd: userInfo.cd_firm,
          divCd: cd_biz,
        },
      });

      // 완료/실패 안내창은 callApi가 백엔드 message로 띄운다.
      if (result?.status) await onSearch();
    } catch (error: any) {
    } finally {
      // 성공·실패·중간 return 무엇이든 스피너는 반드시 닫는다.
      setLoading(false);
    }
  };

  const convertToTreeData = (data: any[]) => {
    return data.map((row) => {
      // 사업장 (cd_dept, cd_dept_p 둘 다 null)
      if (!row.cd_dept && !row.cd_dept_p) {
        return {
          ...row,
          cd_dept: row.cd_biz,
          cd_dept_p: null, // 최상위 루트
        };
      }

      // level 1 부서 (cd_dept_p만 null)
      if (row.cd_dept && !row.cd_dept_p) {
        return {
          ...row,
          cd_dept_p: row.cd_biz, // 부모를 사업장으로 지정
        };
      }

      // 하위부서
      return row;
    });
  };

  const onRowDataReady = () => {
    gridRefs.current["tree"].current.selectAndFocusRow(0);
    gridRefs.current["grid"].current.selectAndFocusRow(0);
  };

  const grids = useMemo(
    () => ({
      tree: {
        columns: columns.tree,
        dataSource: dataSources.tree,
        events: {
          onFocusedRowChanged,
          onRowDataReady: onRowDataReady,
        },
      },
      grid: {
        columns: columns.grid,
        dataSource: dataSources.grid,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <CommonSpinner isLoading={loading} />
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="25%">
          <SnTree
            ref={gridRefs.current["tree"]}
            columnConfig={grids.tree.columns}
            rowData={grids.tree.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "부서리스트",
            }}
            header={
              <div>
                <SnButton
                  text="전체전개"
                  onClick={() => onGrid1BtnsClick("btnOpen")}
                />
                <SnButton
                  text="전체닫기"
                  onClick={() => onGrid1BtnsClick("btnClose")}
                />
              </div>
            }
            events={grids.tree.events}
            treeProps={{
              treeDataParentIdField: "cd_dept_p",
              treeIdKey: "cd_dept",
              treeOrderKey: "no_seq_sort",
              groupDefaultExpanded: 5,
              autoGroupColumnDef: autoGroupColumnDef,
              getRowId: getRowId,
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="74.5%">
          <SnGrid
            ref={gridRefs.current["grid"]}
            columnConfig={grids.grid.columns}
            rowData={grids.grid.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세정보",
            }}
            header={
              <div>
                {/* 더존(AM10) 연동 환경에서만 사원정보 가져오기를 노출한다. */}
                {config?.MAS0001?.dc_ctrl === "AM10" && (
                  <SnButton
                      text="가져오기"
                      onClick={() => onGrid2BtnsClick("btnImport")}
                  />
                )}
              </div>
            }
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MAS_ORG_REG_006;
